import { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  createCachedTokenGetter,
  getSdkOrganizationId,
} from "@/lib/auth/cachedAccessToken";
import { hasAuthRedirectSearchParams } from "@/lib/oauth/isAuthCallbackSearch";

/**
 * Returns a token getter that serves the cached access token (refreshed
 * centrally by `TokenRefresher`) and only falls back to the SDK near expiry.
 * Prefer this over calling `useAuth0().getAccessTokenSilently` directly for
 * call sites that just need "the current token" (Authorization headers, reading
 * claims). See {@link createCachedTokenGetter} and LAR-290.
 */
export const useCachedAccessToken = (): (() => Promise<string>) => {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    user,
    isLoading,
    isAuthenticated,
  } = useAuth0();
  const sdkOrganizationId = getSdkOrganizationId(user);
  return useMemo(
    () =>
      createCachedTokenGetter(
        getAccessTokenSilently,
        loginWithRedirect,
        sdkOrganizationId,
        // There is no token to serve, and nothing to recover, unless Auth0 has
        // settled on a session: `isLoading` covers its initial `checkSession` /
        // `handleRedirectCallback`, the URL check covers the window before that
        // effect runs, and `!isAuthenticated` covers the signed-out page. Without
        // the last one, any caller that asks for a token there — a provider
        // reading the tenant for its stylesheet, say — has its failed refresh
        // turned into an interactive login, so a logout bounces the user
        // straight back to the branded sign-in screen (LAR-437).
        () => isLoading || !isAuthenticated || hasAuthRedirectSearchParams()
      ),
    [
      getAccessTokenSilently,
      loginWithRedirect,
      sdkOrganizationId,
      isLoading,
      isAuthenticated,
    ]
  );
};
